import MainLayout from 'components/layout/main';
import FacilitiesFilter from './components/facilitiesFilter';
import { useFacilities2Table } from 'components/table/columns/facilities';
import { useSearchParams } from 'react-router-dom';
import CustomPagination from 'ui/pagination';
import { useGetObjectsQuery } from './api/queries/generated/GetObjects';
import React, { useState } from 'react';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import DraggableColumnHeader from 'components/table/header/draggableColumnHeader';
import NewSkeletonLayout from 'components/table/newSkeleton';
import useFacilityHeaderMenu from 'utils/hooks/headerMenu/useFacilityHeaderMenu';
import { useObject } from 'utils/api/useObject';

const FacilitiesPage = () => {
  const [params] = useSearchParams();
  const { object, id } = useObject();
  const [column, setColumn] = useState('');

  const headerMenu = useFacilityHeaderMenu();

  const sizeParam = params.get('size');
  const pageParam = params.get('page');
  const cityId = params.get('cityId') || undefined;
  const foremanId = params.get('foremanId') || undefined;
  const supervisorId = params.get('supervisorId') || undefined;
  const naimixLegalEntityId = params.get('naimixLegalEntityId') || undefined;
  const activeParam = params.get('active');
  const searchQuery = params.get('search') || undefined;

  const page = pageParam ? +pageParam : 1;
  const size = sizeParam ? +sizeParam : 25;
  const isActive = activeParam ? activeParam === 'true' : undefined;

  const { data: dataObjects, loading: loadingObjects } = useGetObjectsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        parentId: id || undefined,
        limit: size,
        offset: (page - 1) * size,
        cityId,
        foremanId,
        supervisorId,
        naimixLegalEntityId,
        isActive,
        searchQuery,
      },
    },
  });

  const objects = dataObjects?.getObjects?.objects || [];
  const totalCount = dataObjects?.getObjects?.count || 0;
  const totalPages = Math.ceil(totalCount / size);
  const table = useFacilities2Table(objects as any);

  const renderTable = () => {
    if (loadingObjects) {
      return <NewSkeletonLayout table={table} />;
    }

    if (objects.length === 0) {
      return (
        <div className="flex justify-center my-5">
          <h3 className="text-smena_text Body1">По вашему запросу ничего не нашлось</h3>
        </div>
      );
    }

    return (
      <div className="overflow-auto">
        <table
          {...{
            style: {
              width: table.getCenterTotalSize(),
            },
          }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DraggableColumnHeader
                    key={header.id}
                    header={header}
                    table={table}
                    hoverColumn={column}
                    setHoverColumn={setColumn}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                className={clsx(
                  'table__row',
                  row.depth === 1 ? 'bg-smena_gray-2' : row.depth === 2 ? 'bg-smena_gray-5' : '',
                )}
                onClick={() => row.toggleExpanded()}
                style={{ cursor: 'pointer' }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx('table__content', column === cell.column.id ? 'active-column' : '')}
                    onMouseEnter={() => {
                      setColumn(cell.column.id);
                    }}
                    onMouseLeave={() => {
                      setColumn('');
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <MainLayout
      title={object?.name || 'Объекты'}
      breadCrumbs={[
        { path: '/facilities', name: 'Объекты' },
        { path: `/facilities/${id}`, name: object?.name || '' },
      ]}
      headerMenu={id ? headerMenu : undefined}
    >
      <div
        className={clsx(
          'flex flex-col shadow-smena rounded bg-smena_white h-[calc(100vh-190px)]',
          id ? 'h-[calc(100vh-220px)]' : 'h-[calc(100vh-190px)]',
        )}
      >
        <div className="flex-none">
          <FacilitiesFilter table={table} />
        </div>
        <div className="flex-1 overflow-auto">{renderTable()}</div>
        <div className="flex-none">{totalCount > 0 && <CustomPagination currentPage={page} total={totalPages} />}</div>
      </div>
    </MainLayout>
  );
};

export default FacilitiesPage;
